import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, FileBadge } from 'lucide-react';
import { scheduleK1ReportAPI, fundAPI } from '../../services/api';
import { CustomizableReportTable } from '../../components/reports/CustomizableReportTable';

const NUMERIC = new Set(['total_commitment', 'total_called', 'total_contribution', 'total_pending_receipt', 'total_units_issued', 'total_distribution', 'total_nav']);
const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 4 });
const STORAGE_KEY = 'report-cols-schedule-k1-v2';

const toColumns = (catalog) => Object.values(catalog).flatMap((s) => s.fields.map((f) => {
  const col = { key: f.key, label: f.label, section: s.section };
  if (f.key === 'account_name') col.render = (r) => <Link to={`/investors/accounts/${r.account_id}`} className="text-blue-700 hover:underline font-medium">{r.account_name || '—'}</Link>;
  if (NUMERIC.has(f.key)) { col.align = 'right'; col.render = (r) => fmt(r[f.key]); col.exportValue = (r) => r[f.key] ?? 0; }
  return col;
}));

const savedColumns = (fallback) => {
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return Array.isArray(v) && v.length ? v : fallback;
  } catch { return fallback; }
};

export default function ScheduleK1Report() {
  const [funds, setFunds] = useState([]);
  const [fundId, setFundId] = useState('');
  const [columns, setColumns] = useState(null);
  const [defaults, setDefaults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fundAPI.getAll().then((r) => setFunds(r.data || [])).catch(() => setFunds([]));
    scheduleK1ReportAPI.catalog().then(({ data }) => {
      setColumns(toColumns(data.catalog));
      setDefaults(data.default_columns);
      setSelected(savedColumns(data.default_columns));
    });
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    scheduleK1ReportAPI.get({ fundId, columns: selected.length ? selected : ['account_name'] })
      .then((r) => setRows(r.data?.rows || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [fundId, selected]);

  const onVisibleChange = useCallback((v) => {
    setSelected((prev) => (prev && prev.length === v.length && prev.every((k, i) => k === v[i]) ? prev : v));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return q ? rows.filter((r) => Object.values(r).some((v) => String(v ?? '').toLowerCase().includes(q))) : rows;
  }, [rows, search]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="schedule-k1-report-page">
      <Link to="/investors/reports" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4" data-testid="k1-back-link">
        <ArrowLeft size={16} className="mr-1" /> Back to Reports
      </Link>
      <div className="flex items-start gap-3 mb-6">
        <div className="p-2 rounded-lg bg-indigo-600 text-white"><FileBadge size={20} /></div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schedule K-1 Investors</h1>
          <p className="text-sm text-gray-500 mt-1">Investor Accounts with <b>Issue Schedule K-1</b> switched ON under Account → Compliance. Pick columns from any account section and export.</p>
        </div>
      </div>
      <div className="bg-white border rounded-lg p-4 mb-4 w-fit">
        <p className="text-xs text-gray-500 uppercase tracking-wider">Accounts requiring K-1</p>
        <p className="text-2xl font-bold text-indigo-700 mt-1" data-testid="k1-kpi-total">{rows.length}</p>
      </div>
      {!columns ? <div className="p-12 text-center text-gray-500">Loading report...</div> : (
        <div className={loading ? 'opacity-60 pointer-events-none transition-opacity' : 'transition-opacity'}>
          <CustomizableReportTable
            columns={columns} rows={filtered} defaultVisible={defaults} storageKey={STORAGE_KEY}
            fileName="schedule_k1_investors" testId="k1-report" rowKey={(r) => r.account_id} onVisibleChange={onVisibleChange}
            toolbar={(
              <>
                <select value={fundId} onChange={(e) => setFundId(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white" data-testid="k1-fund-filter">
                  <option value="">All Funds</option>
                  {funds.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
                <div className="relative min-w-[240px]">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search any column..." className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" data-testid="k1-search" />
                </div>
              </>
            )}
          />
        </div>
      )}
    </div>
  );
}
